import {View, Text, StyleSheet, TouchableOpacity, ActivityIndicator} from 'react-native';
import {useTranslation} from 'react-i18next';
import {Ionicons} from '@expo/vector-icons';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";

interface PriceInfo {
    price: number;
    currency: string;
    isFree: boolean;
}

interface OfferPurchaseButtonProps {
    priceInfo: PriceInfo;
    canPurchase: boolean;
    isProcessing: boolean;
    hasRequiredAchievements: boolean;
    onPurchase: () => void;
}

export const OfferPurchaseButton = ({
    priceInfo,
    canPurchase,
    isProcessing,
    hasRequiredAchievements,
    onPurchase,
}: OfferPurchaseButtonProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);

    if (!hasRequiredAchievements) {
        return null;
    }

    if (!priceInfo.isFree && !canPurchase) {
        return (
            <View style={styles.notAvailableInfo}>
                <Ionicons name="information-circle-outline" size={20} color={theme.colors.textSecondary} />
                <Text style={styles.notAvailableText}>
                    {t('offers.purchaseWebOnly')}
                </Text>
            </View>
        );
    }

    return (
        <TouchableOpacity
            style={[
                styles.purchaseButton,
                isProcessing && styles.disabledButton
            ]}
            onPress={onPurchase}
            disabled={isProcessing}
        >
            {isProcessing ? (
                <ActivityIndicator color="white" />
            ) : (
                <View style={styles.purchaseButtonContent}>
                    <Text style={styles.purchaseButtonText}>
                        {priceInfo.isFree ? t('offers.claimOffer') : t('offers.purchase')}
                    </Text>
                    {!priceInfo.isFree && (
                        <Ionicons name="arrow-forward" size={20} color="white" style={styles.buttonIcon} />
                    )}
                </View>
            )}
        </TouchableOpacity>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    purchaseButton: {
        backgroundColor: theme.colors.primary,
        paddingVertical: 16,
        paddingHorizontal: 24,
        borderRadius: 12,
        alignItems: 'center',
        marginTop: 20,
    },
    purchaseButtonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    purchaseButtonText: {
        color: 'white',
        fontSize: 18,
        fontWeight: '600',
    },
    buttonIcon: {
        marginLeft: 8,
    },
    disabledButton: {
        opacity: 0.5,
    },
    notAvailableInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 20,
        paddingVertical: 12,
        paddingHorizontal: 16,
        backgroundColor: theme.colors.background,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    notAvailableText: {
        fontSize: 14,
        color: theme.colors.textSecondary,
        marginLeft: 8,
    },
});
